<template>
  <Head
    v-if="!!headModel"
    v-bind="$attrs"
    ref="head"
    :kind="kind"
    :values="headModel.values"
    :valueType="headModel.valueType"
    :showTooltips="showTooltips"
    :animateEntrance="readyToAnimate"
    :fillOverride="fillOverride"
    :containerForTooltips="containerForTooltips"
    :whiteBackground="whiteBackground"
  />
</template>

<script setup lang="ts">
import { computed, onBeforeMount, ref } from "vue";
import d from "debug";
import { HeadBaseApiModel } from "../../api-models";
import { Kind, FillOptions } from "./head-models";
import Head from "./gyre-head.vue";
import useHeadModel from "./useHeadModel";

const debug = d("cs.HeadFromModel");

const props = withDefaults(
  defineProps<{
    headId: string | null;
    kind: Kind;
    showTooltips?: boolean;
    animateEntrance?: boolean;
    fillOverride?: FillOptions | null;
    containerForTooltips?: string;
    whiteBackground?: boolean;
  }>(),
  {
    headId: null,
    showTooltips: true,
    animateEntrance: false,
    fillOverride: null,
    containerForTooltips: "body",
    whiteBackground: false,
  },
);

const emit = defineEmits(["onDataLoaded"]);

// template refs
const head = ref<InstanceType<typeof Head> | null>(null);

const headModel = ref<HeadBaseApiModel | null>(null);

const readyToAnimate = ref(false);

const {
  getSoloHeadModel,
  getViewpointsHeadModel,
  getTeamSoloHeadModel,
  getTeamViewpointsHeadModel,
  getOrgSoloHeadModel,
  getOrgViewpointsHeadModel,
  getTeamEffectivenessIndividualHeadModel,
  getTeamEffectivenessAggregateHeadModel,
} = useHeadModel();

// this is so cms-session can trigger head entrance animation during a session
function playAnimation(areReversing: boolean): void {
  debug("PLAYHEADANIMATION!!!", areReversing);
  if (head.value && !areReversing) {
    head.value.playEntranceAnimationForSessions();
  }
}

function emitDataLoaded(hasData: boolean): void {
  setTimeout(() => {
    emit("onDataLoaded", hasData);
    if (props.animateEntrance) {
      readyToAnimate.value = true;
    }
  }, 0);
}

const sharedCode = computed(() => {
  return new URL(window.location.href).searchParams.get("sharedcode") ?? undefined;
});

function getHeadModelData(): void {
  let modelPromise: Promise<HeadBaseApiModel | null>;
  if (!props.headId) return;

  switch (props.kind) {
    case "solo":
      modelPromise = getSoloHeadModel(props.headId);
      break;
    case "viewpoints":
      modelPromise = getViewpointsHeadModel(props.headId);
      break;
    case "teamsolo":
      modelPromise = getTeamSoloHeadModel(props.headId);
      break;
    case "teamviewpoints":
      modelPromise = getTeamViewpointsHeadModel(props.headId);
      break;
    case "orgsolo":
      modelPromise = getOrgSoloHeadModel(props.headId);
      break;
    case "orgviewpoints":
      modelPromise = getOrgViewpointsHeadModel(props.headId);
      break;
    case "TeamEffectivenessAggregateDiamond":
      modelPromise = getTeamEffectivenessAggregateHeadModel(props.headId, sharedCode.value);
      break;
    case "TeamEffectivenessIndividualDiamond":
      modelPromise = getTeamEffectivenessIndividualHeadModel(props.headId);
      break;
    default:
      throw new Error(`Unexpected remote head kind: ${props.kind}`);
  }

  modelPromise.then((model) => {
    headModel.value = model;
    emitDataLoaded(!!headModel.value?.values);
  });
}

onBeforeMount(() => {
  getHeadModelData();
});

defineExpose({
  getHeadModelData,
  playAnimation,
});
</script>
<style lang="less"></style>
